/**
 * PDF Generation Utility
 * Downloads PDF from backend API (Cloudflare Browser Rendering)
 * The Worker generates HTML and uses Puppeteer to convert to PDF
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Generate and download PDF from form data via backend API
 */
export const downloadTemplatePDF = async ({
  title,
  formData,
  settings = {},
  templateType,
  templateId,
  createdBy,
  selectedSetting,
  allSettings
}) => {
  try {
    // Prepare the data payload with user info
    const dataWithUser = {
      ...formData,
      title: title || formData.title || formData.project_name || templateType,
      created_by_email: createdBy || formData.created_by_email || ''
    };

    // Build query params for settings
    const settingsParam = selectedSetting ? `?settings_id=${selectedSetting}` : '';
    
    // Call the backend PDF generation endpoint
    const response = await axios.post(
      `${API}/api/generate/${templateId}/pdf${settingsParam}`,
      dataWithUser,
      { 
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 60000 // 60 second timeout for PDF generation
      }
    );

    // Check if we got an error response (JSON instead of PDF)
    if (response.headers['content-type']?.includes('application/json')) {
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.details || errorData.error || 'PDF generation failed');
    }

    // Create download link
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    
    // Generate filename
    const docName = formData.project_name || formData.title || formData.meeting_title || 
                    formData.company_name || formData.report_title || templateType || 'document';
    const safeName = docName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `${(templateId || 'document').replace(/-/g, '_')}_${safeName}_${dateStr}.pdf`;
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    return true;
  } catch (error) {
    console.error('PDF generation error:', error);
    
    // If backend PDF fails, show a helpful error
    let errorMessage = 'Unknown error';
    let statusCode = error.response?.status;
    
    if (statusCode === 429) {
      errorMessage = 'Too many PDF requests. Please wait 30 seconds and try again.';
    } else if (statusCode === 503) {
      errorMessage = 'PDF service is temporarily unavailable. Please try again later.';
    } else if (error.response?.data) {
      // Try to read error from blob
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        errorMessage = errorData.details || errorData.error || error.message;
      } catch {
        errorMessage = error.message;
      }
    } else {
      errorMessage = error.message;
    }
    
    throw new Error(`Failed to generate PDF: ${errorMessage}`);
  }
};

// Legacy export for compatibility
export const downloadPDF = downloadTemplatePDF;

export default { downloadTemplatePDF, downloadPDF };
